import React from "react";
import { useIntegrityStream } from "@/lib/useIntegrityStream";
import { Card, Overline, PulseDot, Tag } from "@/components/cathedral/Primitives";

function fmtNumber(n) {
  if (n == null) return "—";
  return new Intl.NumberFormat("en-US").format(n);
}

export default function LiveHeartbeat() {
  const { data, status } = useIntegrityStream({ intervalSeconds: 5 });
  const dissolved = data?.integrity?.dissolved;
  const glow = status !== "live" ? "red" : dissolved ? "red" : "green";

  return (
    <Card className="p-7" glow={glow} data-testid="live-heartbeat">
      <div className="flex items-start justify-between gap-6 flex-wrap">
        <div className="min-w-0">
          <Overline tone={status === "live" ? "green" : "muted"}>
            Cathedral Heartbeat · Live WebSocket
          </Overline>
          <div className={`font-display text-2xl font-light tracking-[0.04em] mt-3 leading-none ${status === "live" ? "text-glow-green" : "text-glow-red"}`}>
            {status === "live"
              ? (dissolved ? "DISSOLVED · ALERT" : "STREAM · LIVE")
              : status === "connecting"
              ? "CONNECTING…"
              : "STREAM · OFFLINE"}
          </div>
          <div className="mt-4 flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--text-3)]">
            <PulseDot color={status === "live" ? "green" : "red"} />
            <span>{status === "live" ? "ticking · every 5s" : status}</span>
          </div>
        </div>

        {data && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-x-8 gap-y-3 font-mono text-[11px]">
            <Stat label="Ledger" value={fmtNumber(data.ledger_count)} tone="cyan" testid="hb-ledger" />
            <Stat label="Deliberations" value={fmtNumber(data.deliberations_count)} tone="violet" testid="hb-delibs" />
            <Stat label="Vessels" value={fmtNumber(data.vessels_mounted)} tone="magenta" testid="hb-vessels" />
            <Stat label="Cycle" value={fmtNumber(data.integrity?.cycle_count)} tone="gold" testid="hb-cycle" />
            <Stat label="In Cycle" value={fmtNumber(data.integrity?.executions_in_cycle)} tone="cyan" testid="hb-incycle" />
            <Stat label="Last Avg" value={data.integrity?.last_cycle_score ?? "—"} tone="green" testid="hb-lastavg" />
            <div className="col-span-2">
              <Overline tone="muted">Signer</Overline>
              <div className="mt-1.5 flex items-center gap-2">
                <Tag tone={data.signer_loaded ? "pass" : "default"}>
                  {data.signer_loaded ? "LOADED" : "UNLOADED"}
                </Tag>
                {data.signer_fingerprint && (
                  <span className="text-[var(--text-2)] text-[10px]">
                    fp · {data.signer_fingerprint.slice(0, 12)}…
                  </span>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </Card>
  );
}

function Stat({ label, value, tone, testid }) {
  return (
    <div data-testid={testid}>
      <Overline tone={tone}>{label}</Overline>
      <div className={`mt-1 font-display text-xl text-[var(--text)] leading-none text-glow-${tone}`}>
        {value}
      </div>
    </div>
  );
}
